import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPostBySlug, blogPosts } from "@/data/blogPosts";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useSeo } from "@/hooks/useSeo";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useSeo(
    post ? `${post.title} | LAIzyDocu` : "Artikel nicht gefunden | LAIzyDocu",
    post ? post.excerpt : "Dieser Artikel wurde nicht gefunden.",
  );

  if (!post) {
    return (
      <div className="min-h-screen bg-stone-50">
        <SiteNav />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-semibold text-emerald-900">Artikel nicht gefunden</h1>
          <Link to="/blog" className="inline-block mt-6">
            <Button className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">Zum Magazin</Button>
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const others = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <SiteNav />
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-14">
        <Link to="/blog" data-testid="blogpost-back" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 mb-8">
          <ArrowLeft className="w-4 h-4" /> Zurück zum Magazin
        </Link>
        <div className="flex items-center gap-2 text-sm text-stone-400 mb-4">
          <span>{new Date(post.date).toLocaleDateString("de-DE")}</span>
          <span className="w-1 h-1 rounded-full bg-stone-300" />
          <span>{post.readingMinutes} Min. Lesezeit</span>
        </div>
        <h1 data-testid="blogpost-title" className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-emerald-900 leading-tight">{post.title}</h1>
        <div className="flex items-center gap-2 mt-5 flex-wrap">
          {post.tags.map((t) => (
            <span key={t} className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-medium">{t}</span>
          ))}
        </div>

        <div className="mt-10 space-y-5">
          {post.body.map((block, i) =>
            block.h ? (
              <h2 key={i} className="font-display text-2xl font-semibold text-emerald-900 pt-4">{block.h}</h2>
            ) : (
              <p key={i} className="text-stone-700 text-lg leading-relaxed">{block.p}</p>
            ),
          )}
        </div>

        {/* CTA */}
        <div className="mt-14 bg-emerald-900 text-white rounded-3xl p-8 md:p-10 text-center">
          <h3 className="font-display text-2xl font-semibold">Bereit für die papierlose Praxis?</h3>
          <p className="text-emerald-100/80 mt-2">Fordern Sie jetzt Ihren Testzugang an.</p>
          <Link to="/testzugang" data-testid="blogpost-cta" className="inline-block mt-6">
            <Button className="h-13 px-7 rounded-xl bg-white text-emerald-900 hover:bg-stone-100 text-base">
              Testzugang anfragen <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>

        {/* More */}
        {others.length > 0 && (
          <div className="mt-14">
            <h3 className="font-display text-xl font-semibold text-emerald-900 mb-5">Weitere Artikel</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {others.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blogpost-more-${p.slug}`}
                  className="group bg-white rounded-2xl border border-stone-100 p-6 hover:shadow-[0_12px_40px_rgb(0,0,0,0.05)] transition-shadow block">
                  <h4 className="font-display text-base font-semibold text-emerald-900 group-hover:text-emerald-700 leading-snug">{p.title}</h4>
                  <p className="text-stone-500 mt-1 text-sm line-clamp-2">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
      <SiteFooter />
    </div>
  );
}
